import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { convertCompleteBracesIn, mergeText } from '../../msg/tokens';
import type { MsgMenuEntry, MsgToken } from '../../msg/tokens';

export interface MsgEditorProps<K extends string = string> {
  value: MsgToken<K>[];
  onChange: (next: MsgToken<K>[]) => void;
  menu: MsgMenuEntry<K>[];
  placeholder?: string;
  rows?: number;
  ariaLabel: string;
  /** Lets the preview show `choosing a field…` while the menu is open. */
  onMenuOpenChange?: (open: boolean) => void;
  /** Right to left, for a body being written in Farsi. */
  rtl?: boolean;
}

/**
 * The message editor (spec 21 §3.5, rebuilt after testing — spec 23 §2.1–2.4,
 * then **lifted out of the recall module** for amendment 25 §9).
 *
 * It takes its field list as a prop rather than importing one, which is what lets
 * the reminders editor mount the same box over appointment fields. Nothing in
 * here knows what a recall is.
 *
 * **The first version was a row of separate text inputs with chips between them**,
 * one input per run of text. It typechecked and it was unusable: you could not
 * click the empty part of the box, there was nowhere to type after the last chip
 * or between two adjacent ones, and a subject line rendered as a 13px-wide input
 * whose placeholder read `W`. The token-array model was right; **rendering it as
 * one input per token was the mistake.**
 *
 * This is one `contenteditable` with chips as atomic `contenteditable=false`
 * spans — the other implementation spec 16 §3.1a allows. The rules it exists to
 * protect are unchanged:
 *
 * - a field arrives only from the menu and renders as a chip, never as braces;
 * - a chip is atomic — one Backspace removes the whole thing;
 * - braces that match no field survive as a token that **blocks the step**.
 *
 * The DOM is the source of truth while focused, and the value is only written
 * back in when it changes from outside — re-rendering on every keystroke is what
 * throws the caret to the start of the box.
 */
export function MsgEditor<K extends string = string>({
  value,
  onChange,
  menu,
  placeholder,
  rows = 3,
  ariaLabel,
  onMenuOpenChange,
  rtl = false,
}: MsgEditorProps<K>) {
  // The field names this box knows — from the menu, not from a global map, so a
  // brace typed by hand is checked against THIS editor's fields.
  const known = useMemo(() => new Set(menu.map((m) => m.field.key)), [menu]);
  const labelOf = useMemo(() => {
    const m = new Map(menu.map((e) => [e.field.key, e.field]));
    return (key: K) => m.get(key);
  }, [menu]);
  const box = useRef<HTMLDivElement | null>(null);
  const savedRange = useRef<Range | null>(null);
  const lastEmitted = useRef<string>('');

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAt, setMenuAt] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [empty, setEmpty] = useState(value.length === 0);

  useEffect(() => { onMenuOpenChange?.(menuOpen); }, [menuOpen, onMenuOpenChange]);

  /** Paint tokens into the box. Only when the value differs from what we emitted. */
  useEffect(() => {
    const el = box.current;
    if (el === null) return;
    const signature = JSON.stringify(value);
    if (signature === lastEmitted.current) return;
    el.innerHTML = '';
    for (const node of value) {
      if (node.kind === 'text') el.append(document.createTextNode(node.text));
      else if (node.kind === 'field') el.append(chipNode(node.field, labelOf(node.field)));
      else el.append(unknownNode(node.raw));
    }
    lastEmitted.current = signature;
    setEmpty(el.textContent === '');
  }, [value, labelOf]);

  /** Read the DOM back into tokens. The chips carry their key on a data attribute. */
  const serialise = useCallback((): MsgToken<K>[] => {
    const el = box.current;
    if (el === null) return [];
    const out: MsgToken<K>[] = [];
    el.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent ?? '';
        if (text !== '') out.push({ kind: 'text', text });
        return;
      }
      if (node instanceof HTMLElement) {
        const key = node.dataset.field;
        if (key !== undefined) out.push({ kind: 'field', field: key as K });
        else if (node.dataset.unknown !== undefined) out.push({ kind: 'unknown', raw: node.textContent });
        else {
          // A stray element — a pasted <b>, a browser's <br>. Keep its words.
          const text = node.textContent;
          if (text !== '') out.push({ kind: 'text', text });
        }
      }
    });
    return mergeText(out);
  }, []);

  const emit = useCallback(
    (tokens: MsgToken<K>[]) => {
      lastEmitted.current = JSON.stringify(tokens);
      onChange(tokens);
    },
    [onChange],
  );

  const closeMenu = () => {
    setMenuOpen(false);
    setQuery('');
    setHighlight(0);
  };

  /** Where the caret is, in the box's own coordinates, so the menu lands there. */
  const caretPosition = (): { left: number; top: number } => {
    const el = box.current;
    const sel = window.getSelection();
    if (el === null || sel === null || sel.rangeCount === 0) return { left: 0, top: 0 };
    const range = sel.getRangeAt(0).cloneRange();
    const rect = range.getBoundingClientRect();
    const host = el.getBoundingClientRect();
    // A collapsed range at the start of an empty line has a zero rect; fall back
    // to the box's own left edge so the menu never lands in the far corner.
    const left = rect.left === 0 ? 0 : rect.left - host.left;
    const top = (rect.bottom === 0 ? host.height : rect.bottom - host.top) + 4;
    return { left: Math.max(0, Math.min(left, host.width - 320)), top };
  };

  const handleInput = () => {
    const tokens = convertCompleteBracesIn(serialise(), known);
    // A complete `{first_name}` typed or pasted becomes a chip immediately.
    const hadConversion = JSON.stringify(tokens) !== JSON.stringify(serialise());
    emit(tokens);
    setEmpty((box.current?.textContent ?? '') === '');

    if (hadConversion) {
      repaint(tokens);
      closeMenu();
      return;
    }

    // An unclosed `{` at the caret opens the menu and filters as you type.
    const before = textBeforeCaret();
    const open = /\{([^{}]*)$/.exec(before);
    if (open) {
      savedRange.current = currentRange();
      setMenuAt(caretPosition());
      setMenuOpen(true);
      setQuery(open[1] ?? '');
      setHighlight(0);
    } else if (menuOpen) {
      closeMenu();
    }
  };

  /** Rewrite the box from tokens and put the caret at the end. */
  const repaint = (tokens: MsgToken<K>[]) => {
    const el = box.current;
    if (el === null) return;
    el.innerHTML = '';
    for (const node of tokens) {
      if (node.kind === 'text') el.append(document.createTextNode(node.text));
      else if (node.kind === 'field') el.append(chipNode(node.field, labelOf(node.field)));
      else el.append(unknownNode(node.raw));
    }
    lastEmitted.current = JSON.stringify(tokens);
    placeCaretAtEnd(el);
  };

  /** Insert a chip where the `{` was typed, swallowing the `{` and the query. */
  const insertField = (key: K) => {
    const el = box.current;
    if (el === null) return;
    el.focus();
    const range = savedRange.current ?? currentRange();
    if (range !== null) {
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
    // Delete the `{` and anything typed after it, then drop the chip in.
    deleteBeforeCaret(/\{[^{}]*$/.exec(textBeforeCaret())?.[0].length ?? 0);
    const chip = chipNode(key, labelOf(key));
    insertNodeAtCaret(chip);
    // A trailing space so there is somewhere to carry on typing after the chip.
    insertNodeAtCaret(document.createTextNode(' '));
    emit(convertCompleteBracesIn(serialise(), known));
    setEmpty(false);
    closeMenu();
  };

  const filtered =
    query.trim() === ''
      ? menu
      : menu.filter((m) => m.field.label.toLowerCase().includes(query.trim().toLowerCase()));
  const selectable = filtered.filter((m) => m.unavailable === undefined);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (menuOpen) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlight((h) => Math.min(h + 1, Math.max(0, selectable.length - 1)));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlight((h) => Math.max(0, h - 1));
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        const pick = selectable[highlight];
        if (pick) insertField(pick.field.key);
        return;
      }
      if (e.key === 'Escape') {
        // Leaves the `{` as typed, which then reads as an unknown field and
        // blocks the step, rather than quietly disappearing.
        e.preventDefault();
        closeMenu();
        return;
      }
    }
    // Enter never makes a new line: a message is one block of words.
    if (e.key === 'Enter') e.preventDefault();
  };

  return (
    <div className="relative">
      <div
        ref={box}
        role="textbox"
        aria-multiline={rows > 1}
        aria-label={ariaLabel}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onKeyDown={onKeyDown}
        onBlur={() => { savedRange.current = currentRange(); }}
        onClick={(e) => {
          // Clicking a chip SELECTS it — the first version deleted it on click,
          // with nothing on screen to say that it would.
          const target = e.target as HTMLElement;
          if (target.dataset.field !== undefined || target.dataset.unknown !== undefined) {
            selectNode(target);
          }
        }}
        data-placeholder={placeholder ?? ''}
        className={cn(
          'w-full cursor-text rounded-[12px] border border-[#dfe4ee] bg-[var(--surface-card)] px-4 py-3 transition-[border-color,box-shadow]',
          'text-[14px] leading-[1.85] text-[var(--text-primary)] outline-none hover:border-[#ccd4e4] focus:border-[var(--brand-indigo)] focus:shadow-[0_0_0_3px_rgba(76,91,212,0.14)]',
          'empty:before:text-[var(--text-muted)] empty:before:content-[attr(data-placeholder)]',
        )}
        dir={rtl ? 'rtl' : undefined}
        style={{ minHeight: rows * 26 + 20, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
      />
      {/* The placeholder only shows while the box is genuinely empty — `:empty`
          alone misses a box holding a single empty text node. */}
      {empty && (placeholder ?? '') !== '' ? (
        <span className="pointer-events-none absolute left-[15px] top-[11px] text-[13.5px] text-[var(--text-muted)]">
          {placeholder}
        </span>
      ) : null}

      {menuOpen ? (
        <FieldMenu
          entries={filtered}
          highlight={highlight}
          at={menuAt}
          onPick={insertField}
          onClose={closeMenu}
        />
      ) : null}

      <button
        type="button"
        onMouseDown={(e) => { e.preventDefault(); }}
        onClick={() => {
          const el = box.current;
          if (el === null) return;
          el.focus();
          if (savedRange.current === null) placeCaretAtEnd(el);
          insertNodeAtCaret(document.createTextNode('{'));
          savedRange.current = currentRange();
          setMenuAt(caretPosition());
          setMenuOpen(true);
          setQuery('');
          setHighlight(0);
        }}
        className="mt-1.5 inline-flex items-center gap-1 text-[11.5px] font-semibold text-[var(--brand-indigo)] hover:underline"
      >
        <Icon kind="plus" size={11} stroke={2.4} />
        Insert field
      </button>
      <span className="ml-2 text-[11.5px] text-[var(--text-muted)]">
        Type <span className="font-[var(--font-mono)] text-[var(--text-secondary)]">{'{'}</span> anywhere to insert a
        patient’s details.
      </span>
    </div>
  );
}

/** The menu, positioned at the caret rather than under the button (§2.3). */
function FieldMenu<K extends string>({
  entries,
  highlight,
  at,
  onPick,
  onClose,
}: {
  entries: MsgMenuEntry<K>[];
  highlight: number;
  at: { left: number; top: number };
  onPick: (key: K) => void;
  onClose: () => void;
}) {
  let selectableSeen = -1;
  return (
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} aria-hidden />
      <div
        className="absolute z-[61] w-[320px] overflow-hidden rounded-[12px] border border-[var(--border-default)] bg-[var(--surface-card)] shadow-[0_12px_32px_rgba(36,41,54,.16)]"
        style={{ left: at.left, top: at.top }}
      >
        <div className="border-b border-[var(--border-default)] px-3 py-2 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--text-muted)]">
          Insert a field · ↑↓ then Enter
        </div>
        <div className="max-h-[240px] overflow-y-auto py-1">
          {entries.length === 0 ? (
            <div className="px-3 py-3 text-[12px] text-[var(--text-muted)]">No field matches that.</div>
          ) : (
            entries.map((m) => {
              const disabled = m.unavailable !== undefined;
              if (!disabled) selectableSeen += 1;
              const active = !disabled && selectableSeen === highlight;
              return (
                <button
                  key={m.field.key}
                  type="button"
                  disabled={disabled}
                  onMouseDown={(e) => { e.preventDefault(); }}
                  onClick={() => { onPick(m.field.key); }}
                  className={cn(
                    'flex w-full items-baseline justify-between gap-3 px-3 py-1.5 text-left',
                    disabled
                      ? 'cursor-not-allowed opacity-55'
                      : active
                        ? 'bg-[var(--brand-indigo-tint)]'
                        : 'hover:bg-[var(--surface-sunken)]',
                  )}
                >
                  <span className="text-[12.5px] font-semibold text-[var(--text-primary)]">{m.field.label}</span>
                  {/* Greyed WITH the reason, never hidden. */}
                  <span className="shrink-0 text-right text-[11px] text-[var(--text-muted)]">
                    {m.unavailable ?? m.field.example}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}

/* ---- DOM helpers --------------------------------------------------------- */

function chipNode(key: string, def: { label: string; example: string } | undefined): HTMLElement {
  const el = document.createElement('span');
  el.dataset.field = key;
  el.contentEditable = 'false';
  el.textContent = def?.label ?? key;
  el.title = def === undefined ? key : `${def.label} — for example, ${def.example}`;
  el.className =
    'mx-[1px] inline-block cursor-pointer select-none rounded-[6px] bg-[var(--brand-indigo-tint)] px-1.5 py-[1px] align-baseline text-[12px] font-semibold text-[var(--brand-indigo)]';
  return el;
}

/** Braces that match no field: red, wavy-underlined, and the step cannot start. */
function unknownNode(raw: string): HTMLElement {
  const el = document.createElement('span');
  el.dataset.unknown = 'true';
  el.contentEditable = 'false';
  el.textContent = raw;
  el.title = `${raw} isn’t a field`;
  el.className = 'mx-[1px] inline-block cursor-pointer select-none align-baseline text-[var(--alert-red)]';
  el.style.textDecoration = 'underline wavy var(--alert-red)';
  return el;
}

function currentRange(): Range | null {
  const sel = window.getSelection();
  return sel !== null && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
}

function textBeforeCaret(): string {
  const range = currentRange();
  if (range === null) return '';
  const node = range.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) return '';
  return (node.textContent ?? '').slice(0, range.startOffset);
}

/** Remove `n` characters immediately before the caret, inside its own text node. */
function deleteBeforeCaret(n: number): void {
  if (n <= 0) return;
  const range = currentRange();
  if (range === null) return;
  const node = range.startContainer;
  if (node.nodeType !== Node.TEXT_NODE) return;
  const start = Math.max(0, range.startOffset - n);
  range.setStart(node, start);
  range.deleteContents();
  range.collapse(true);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

function insertNodeAtCaret(node: Node): void {
  const range = currentRange();
  if (range === null) return;
  range.deleteContents();
  range.insertNode(node);
  range.setStartAfter(node);
  range.collapse(true);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

function placeCaretAtEnd(el: HTMLElement): void {
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

function selectNode(el: HTMLElement): void {
  const range = document.createRange();
  range.selectNode(el);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}
